import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { Container, Loading, SimpleButton, ErrorMessage, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { useAuth } from '@/src/app/context/useAuth';
import { Constants } from '@/src/utils';
import type { SchoolBaseDTO, SchoolCreateDTO } from './models/schoolDTOs';
import SchoolsTable from './components/SchoolsTable';
import CreateEditSchoolModal from './components/CreateEditSchoolModal';
import DeleteSchoolModal from './components/DeleteSchoolModal';
import SchoolFilters from './components/SchoolFilters';

const SchoolsPage: React.FC = () => {
    document.title = 'Schools';

    const { user } = useAuth();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [schools, setSchools] = useState<SchoolBaseDTO[]>([]);
    const [filteredSchools, setFilteredSchools] = useState<SchoolBaseDTO[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState<SchoolBaseDTO | null>(null);
    const userId = user?.id || 'nouser';
    const FILTER_KEY = `schools-filters-${userId}`;
    const [districtId, setDistrictId] = useState<number>(() => {
        try {
            const saved = localStorage.getItem(FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (typeof parsed.districtId === 'number') return parsed.districtId;
            }
        } catch {
            // intentionally empty
        }
        return 0;
    });
    const [searchString, setSearchString] = useState<string>('');

    const refreshSchools = useCallback(async () => {
        setError('');
        setLoaded(false);
        try {
            let url = 'dbo/schools';
            const params = [];
            if (districtId) params.push(`districtId=${districtId}`);
            if (params.length) url += '?' + params.join('&');
            const res = await apiRequest<SchoolBaseDTO[]>('GET', url);
            setSchools(res.data ?? []);
        } catch {
            setError('Failed to fetch schools');
        } finally {
            setLoaded(true);
        }
    }, [districtId]);

    const handleCreate = async (school: SchoolCreateDTO) => {
        setError('');
        try {
            await apiRequest('POST', 'dbo/schools', school);
            setShowCreateModal(false);
            refreshSchools();
        } catch {
            setError('Failed to create school');
        }
    };

    const handleEdit = async (id: number, school: SchoolBaseDTO) => {
        setError('');
        try {
            await apiRequest('PUT', `dbo/schools/${id}`, school);
            setShowEditModal(false);
            setSelectedSchool(null);
            refreshSchools();
        } catch {
            setError('Failed to update school');
        }
    };

    const handleDelete = async (id: number) => {
        setError('');
        try {
            await apiRequest('DELETE', `dbo/schools/${id}`);
            setShowDeleteModal(false);
            setSelectedSchool(null);
            refreshSchools();
        } catch {
            setError('Failed to delete school');
        }
    };

    useEffect(() => {
        refreshSchools();
    }, [refreshSchools]);

    useEffect(() => {
        if (!userId) return;
        try {
            localStorage.setItem(FILTER_KEY, JSON.stringify({ districtId }));
        } catch {
            // intentionally empty
        }
    }, [districtId, userId, FILTER_KEY]);

    useEffect(() => {
        const lowerSearch = searchString.toLowerCase();
        setFilteredSchools(
            schools.filter(
                (school) =>
                    school.name.toLowerCase().includes(lowerSearch) ||
                    school.code.toLowerCase().includes(lowerSearch) ||
                    school.districtName.toLowerCase().includes(lowerSearch)
            )
        );
    }, [schools, searchString]);

    return (
        <Container>
            <h1 className="page-header">Schools</h1>
            <br />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    gap: 2,
                    flexWrap: 'wrap', // responsive
                }}
            >
                {/* Filter + Search on the left */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolFilters districtId={districtId} setDistrictId={setDistrictId} />
                    <SimpleTextField
                        label="Search..."
                        name="searchDistricts"
                        variant="outlined"
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                    />
                </Box>

                {user?.role === Constants.ROLES.SUPER_ADMIN && (
                    <SimpleButton
                        onClick={() => setShowCreateModal(true)}
                        variant="contained"
                        color="primary"
                        className="whitespace-nowrap"
                    >
                        Add School
                    </SimpleButton>
                )}
            </Box>

            {!loaded && <Loading message="Loading schools..." />}
            <ErrorMessage error={error} />
            {loaded && (
                <SchoolsTable
                    data={filteredSchools}
                    onEdit={(row) => {
                        setSelectedSchool(row);
                        setShowEditModal(true);
                    }}
                    onDelete={(row) => {
                        setSelectedSchool(row);
                        setShowDeleteModal(true);
                    }}
                />
            )}

            {showCreateModal && (
                <CreateEditSchoolModal
                    open={showCreateModal}
                    mode="create"
                    onSave={(school) => handleCreate(school as SchoolCreateDTO)}
                    onClose={() => setShowCreateModal(false)}
                    error={error}
                />
            )}
            {showEditModal && selectedSchool && selectedSchool.id != null && (
                <CreateEditSchoolModal
                    open={showEditModal}
                    mode="edit"
                    school={selectedSchool}
                    onSave={(school) => handleEdit(selectedSchool.id!, school as SchoolBaseDTO)}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedSchool(null);
                    }}
                    error={error}
                />
            )}
            {showDeleteModal && selectedSchool && (
                <DeleteSchoolModal
                    open={showDeleteModal}
                    school={selectedSchool}
                    onDelete={() =>
                        selectedSchool.id != null ? handleDelete(selectedSchool.id) : undefined
                    }
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedSchool(null);
                    }}
                    error={error}
                />
            )}
        </Container>
    );
};

export default SchoolsPage;
